import React, { useMemo, useState } from "react";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
} from "@tanstack/react-table";
import { Loader, MessageBanner, Button } from "@src/components/atoms";
import { CardContainer } from "@src/components/cards";
import { SignolUtils } from "@src/utils";

const QueryResults = ({
  results,
  error,
  isLoading,
  columnAliases = {},
  headersFormat = "alias",
  onDownloadCSV = null,
  downloadLoading = false,
  downloadError = null,
  totalRowCount = null,
}) => {
  const [sorting, setSorting] = useState([]);

  // Build dynamic columns from query results
  const columns = useMemo(() => {
    if (!results?.schema) return [];

    // Add row index column at the start
    const indexColumn = {
      header: "#",
      id: "rowIndex",
      cell: (props) => (
        <div className="p-2 text-gray-400 font-mono text-xs">
          {props.row.index + 1}
        </div>
      ),
      enableSorting: false,
      size: 10,
    };

    const dataColumns = results.schema.map((col) => {
      const colName = col.name;
      // Determine display name based on headersFormat
      const displayName =
        headersFormat === "raw"
          ? colName
          : columnAliases[colName] || col.alias || colName;

      return {
        header: displayName,
        accessorKey: colName,
        cell: (props) => {
          const value = props.getValue();
          if (value == null) return <div className="px-2 py-1">-</div>;

          // Check if value is numeric
          const numValue = parseFloat(value);
          if (!isNaN(numValue) && typeof value === "number") {
            // Format to max 4 decimals, remove trailing zeros
            const formatted = parseFloat(numValue.toFixed(4));
            return <div className="px-2 py-1">{formatted}</div>;
          }

          return <div className="px-2 py-1 font-mono">{value.toString()}</div>;
        },
        enableSorting: true,
        enableResizing: true,
        size: 200, // Default column width
        minSize: 100,
        maxSize: 600,
      };
    });

    return [indexColumn, ...dataColumns];
  }, [results?.schema, columnAliases, headersFormat]);

  // Limit preview to 100 rows (but only show ~20 rows in viewport)
  const previewData = useMemo(() => {
    const rows = results?.rows || [];
    return rows.slice(0, 100);
  }, [results?.rows]);

  // Table setup
  const table = useReactTable({
    data: previewData,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableSortingRemoval: false,
  });

  // Error Display
  if (error) {
    return (
      <div className="m-2">
        <MessageBanner
          color="error"
          title="Query Error"
          message={
            error?.response?.data?.message ||
            error?.message ||
            (typeof error === "string"
              ? error
              : JSON.stringify(error, null, 2)) ||
            "An error occurred while executing the query"
          }
          icon="warning"
        />
      </div>
    );
  }

  // No results yet - don't show anything if query hasn't been executed
  if (!results?.rows && !isLoading) {
    return null;
  }

  // Loading State
  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader size="xl" text="Fetching data..." textColor="black" />
      </div>
    );
  }

  // Empty State
  if (results.rows.length === 0) {
    return (
      <CardContainer padding={8}>
        <div className="text-center text-gray-500">
          <p className="text-lg font-semibold mb-2">No Results Found</p>
          <p>Your query returned no data. Try adjusting your query.</p>
        </div>
      </CardContainer>
    );
  }

  // Results Table
  return (
    <>
      <div className="bg-white rounded-lg overflow-hidden border border-gray-200">
        <div
          className="overflow-x-auto overflow-y-auto"
          style={{ maxHeight: "calc(11 * 3rem)" }}
        >
          <table className="w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0 z-10">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className={`p-3 text-left text-xs font-bold text-gray-700 tracking-wider bg-gray-100 border border-gray-200 relative ${
                        header.column.getCanSort()
                          ? "cursor-pointer hover:text-primary-500"
                          : ""
                      }`}
                      style={{ width: `${header.getSize()}px` }}
                    >
                      <div
                        onClick={header.column.getToggleSortingHandler()}
                        className="flex items-center gap-1"
                      >
                        <span
                          className="truncate"
                          title={flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                        </span>
                        {header.column.getIsSorted() && (
                          <span className="text-primary-500 flex-shrink-0">
                            {header.column.getIsSorted() === "asc" ? "↑" : "↓"}
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id} className="hover:bg-gray-50 transition-colors">
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className="text-xs text-gray-700 whitespace-nowrap truncate border border-gray-200"
                      style={{ width: `${cell.column.getSize()}px` }}
                      title={cell.getValue()?.toString() || ""}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {onDownloadCSV && (
        <div className="my-4">
          <p className="text-sm text-gray-700 mb-3">
            Previewing {Math.min(previewData.length, 1000).toLocaleString()} of{" "}
            {(
              totalRowCount ||
              results.row_count ||
              results.total_count ||
              results.rows.length
            ).toLocaleString()}{" "}
            rows. Download CSV for full dataset.
          </p>
          {downloadError && (
            <div className="mb-3 p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm">
              Failed to generate download link. Please try again.
            </div>
          )}
          <Button
            onClick={onDownloadCSV}
            color="primary"
            padding={false}
            block={false}
            loading={downloadLoading}
            {...SignolUtils.makeTrackableDataAttributes({
              actionKey: "MANAGER_DATA_DOWNLOAD_CSV_BUTTON",
              columns: columns.map((col) => col.accessorKey).join(","),
              totalRowCount,
            })}
          >
            Download CSV
          </Button>
        </div>
      )}
    </>
  );
};

export default QueryResults;
